import { FC, useState } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    TablePagination,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Stack,
    Switch,
    FormControlLabel,
    Tooltip,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { useAppVersions, useCreateAppVersion, useUpdateAppVersion, useDeleteAppVersion } from '../../hooks/useAppVersion';
import type { AppVersionItem, AppPlatform, UpdateType, CreateAppVersionRequest, UpdateAppVersionRequest } from '../../types/appVersion';
import { PLATFORM_LABELS, UPDATE_TYPE_LABELS } from '../../types/appVersion';
import {
    formatKstDateTime,
    kstInputToUtcLocalDateTime,
    nowKstDateTimeInputValue,
    toKstDateTimeInputValue,
} from '../../utils/dateTime';

const UPDATE_TYPE_COLORS: Record<UpdateType, 'error' | 'warning' | 'default'> = {
    REQUIRED: 'error',
    OPTIONAL: 'warning',
    NONE: 'default',
};

const formatDate = (dateStr?: string) => {
    return formatKstDateTime(dateStr);
};

interface FormState {
    platform: AppPlatform;
    versionCode: string;
    versionName: string;
    minRequiredVersionCode: string;
    updateType: UpdateType;
    updateTitle: string;
    updateMessage: string;
    storeUrl: string;
    isActive: boolean;
    releasedAt: string;
}

const defaultForm = (platform: AppPlatform = 'ANDROID'): FormState => ({
    platform,
    versionCode: '',
    versionName: '',
    minRequiredVersionCode: '',
    updateType: 'NONE',
    updateTitle: '',
    updateMessage: '',
    storeUrl: '',
    isActive: true,
    releasedAt: nowKstDateTimeInputValue(),
});

const versionToForm = (v: AppVersionItem): FormState => ({
    platform: v.platform,
    versionCode: v.versionCode.toString(),
    versionName: v.versionName,
    minRequiredVersionCode: v.minRequiredVersionCode.toString(),
    updateType: v.updateType,
    updateTitle: v.updateTitle ?? '',
    updateMessage: v.updateMessage ?? '',
    storeUrl: v.storeUrl ?? '',
    isActive: v.isActive,
    releasedAt: toKstDateTimeInputValue(v.releasedAt),
});

export const AppVersionList: FC = () => {
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(20);
    const [platformFilter, setPlatformFilter] = useState<AppPlatform | ''>('');
    const [dialogMode, setDialogMode] = useState<'create' | 'edit' | null>(null);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [form, setForm] = useState<FormState>(defaultForm());

    const { data, isLoading } = useAppVersions({
        page,
        size,
        ...(platformFilter ? { platform: platformFilter as AppPlatform } : {}),
    });
    const createVersion = useCreateAppVersion();
    const updateVersion = useUpdateAppVersion();
    const deleteVersion = useDeleteAppVersion();

    const versions = data?.content ?? [];
    const totalCount = data?.totalCount ?? 0;

    const handleOpenCreate = () => {
        setForm(defaultForm(platformFilter as AppPlatform || 'ANDROID'));
        setEditingId(null);
        setDialogMode('create');
    };

    const handleOpenEdit = (v: AppVersionItem) => {
        setForm(versionToForm(v));
        setEditingId(v.id);
        setDialogMode('edit');
    };

    const handleClose = () => {
        setDialogMode(null);
        setEditingId(null);
    };

    const handleDelete = (id: number, versionName: string) => {
        if (!window.confirm(`"${versionName}" 버전을 삭제하시겠습니까?`)) return;
        deleteVersion.mutate(id);
    };

    const handleSave = () => {
        if (dialogMode === 'create') {
            const req: CreateAppVersionRequest = {
                platform: form.platform,
                versionCode: parseInt(form.versionCode),
                versionName: form.versionName,
                minRequiredVersionCode: parseInt(form.minRequiredVersionCode),
                updateType: form.updateType,
                updateTitle: form.updateTitle || undefined,
                updateMessage: form.updateMessage || undefined,
                storeUrl: form.storeUrl || undefined,
                releasedAt: kstInputToUtcLocalDateTime(form.releasedAt) ?? '',
            };
            createVersion.mutate(req, { onSuccess: handleClose });
        } else if (dialogMode === 'edit' && editingId != null) {
            const req: UpdateAppVersionRequest = {
                versionName: form.versionName,
                minRequiredVersionCode: parseInt(form.minRequiredVersionCode),
                updateType: form.updateType,
                updateTitle: form.updateTitle || undefined,
                updateMessage: form.updateMessage || undefined,
                storeUrl: form.storeUrl || undefined,
                isActive: form.isActive,
                releasedAt: kstInputToUtcLocalDateTime(form.releasedAt) ?? '',
            };
            updateVersion.mutate({ id: editingId, data: req }, { onSuccess: handleClose });
        }
    };

    const isPending = createVersion.isPending || updateVersion.isPending;
    const isFormValid = form.versionName && form.versionCode && form.minRequiredVersionCode && form.releasedAt;

    return (
        <Box p={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">버전 관리</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
                    버전 등록
                </Button>
            </Box>

            {/* Filters */}
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>플랫폼</InputLabel>
                    <Select
                        value={platformFilter}
                        label="플랫폼"
                        onChange={(e) => { setPlatformFilter(e.target.value as AppPlatform | ''); setPage(1); }}
                    >
                        <MenuItem value="">전체</MenuItem>
                        {(Object.keys(PLATFORM_LABELS) as AppPlatform[]).map((p) => (
                            <MenuItem key={p} value={p}>{PLATFORM_LABELS[p]}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Stack>

            {/* Table */}
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>플랫폼</TableCell>
                            <TableCell>버전 이름</TableCell>
                            <TableCell>버전 코드</TableCell>
                            <TableCell>최소 필수 코드</TableCell>
                            <TableCell>업데이트 타입</TableCell>
                            <TableCell>상태</TableCell>
                            <TableCell>배포일</TableCell>
                            <TableCell align="center">작업</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                    로딩 중...
                                </TableCell>
                            </TableRow>
                        ) : versions.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                    등록된 버전이 없습니다.
                                </TableCell>
                            </TableRow>
                        ) : (
                            versions.map((v) => (
                                <TableRow key={v.id} hover>
                                    <TableCell>{v.id}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={PLATFORM_LABELS[v.platform]}
                                            color={v.platform === 'ANDROID' ? 'success' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={600}>{v.versionName}</Typography>
                                    </TableCell>
                                    <TableCell>{v.versionCode}</TableCell>
                                    <TableCell>{v.minRequiredVersionCode}</TableCell>
                                    <TableCell>
                                        <Tooltip title={v.updateTitle ?? ''}>
                                            <Chip
                                                label={UPDATE_TYPE_LABELS[v.updateType]}
                                                color={UPDATE_TYPE_COLORS[v.updateType]}
                                                size="small"
                                                variant={v.updateType === 'NONE' ? 'outlined' : 'filled'}
                                            />
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={v.isActive ? '활성' : '비활성'}
                                            color={v.isActive ? 'success' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption">{formatDate(v.releasedAt)}</Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton size="small" onClick={() => handleOpenEdit(v)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => handleDelete(v.id, v.versionName)}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page - 1}
                    onPageChange={(_, newPage) => setPage(newPage + 1)}
                    rowsPerPage={size}
                    onRowsPerPageChange={(e) => { setSize(parseInt(e.target.value, 10)); setPage(1); }}
                    rowsPerPageOptions={[10, 20, 50]}
                    labelRowsPerPage="페이지당"
                />
            </TableContainer>

            {/* Create / Edit Dialog */}
            <Dialog open={dialogMode !== null} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>{dialogMode === 'create' ? '버전 등록' : '버전 수정'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Stack direction="row" spacing={2}>
                            <FormControl size="small" fullWidth disabled={dialogMode === 'edit'}>
                                <InputLabel>플랫폼</InputLabel>
                                <Select
                                    value={form.platform}
                                    label="플랫폼"
                                    onChange={(e) => setForm({ ...form, platform: e.target.value as AppPlatform })}
                                >
                                    {(Object.keys(PLATFORM_LABELS) as AppPlatform[]).map((p) => (
                                        <MenuItem key={p} value={p}>{PLATFORM_LABELS[p]}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                label="버전 코드"
                                type="number"
                                value={form.versionCode}
                                onChange={(e) => setForm({ ...form, versionCode: e.target.value })}
                                fullWidth
                                required
                                disabled={dialogMode === 'edit'}
                                helperText="숫자 (예: 210)"
                            />
                        </Stack>
                        <Stack direction="row" spacing={2}>
                            <TextField
                                label="버전 이름"
                                value={form.versionName}
                                onChange={(e) => setForm({ ...form, versionName: e.target.value })}
                                fullWidth
                                required
                                helperText="예: 2.1.0"
                                inputProps={{ maxLength: 20 }}
                            />
                            <TextField
                                label="최소 필수 버전 코드"
                                type="number"
                                value={form.minRequiredVersionCode}
                                onChange={(e) => setForm({ ...form, minRequiredVersionCode: e.target.value })}
                                fullWidth
                                required
                                helperText="이 미만은 강제 업데이트"
                            />
                        </Stack>
                        <FormControl size="small" fullWidth>
                            <InputLabel>업데이트 타입</InputLabel>
                            <Select
                                value={form.updateType}
                                label="업데이트 타입"
                                onChange={(e) => setForm({ ...form, updateType: e.target.value as UpdateType })}
                            >
                                {(Object.keys(UPDATE_TYPE_LABELS) as UpdateType[]).map((t) => (
                                    <MenuItem key={t} value={t}>{UPDATE_TYPE_LABELS[t]}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {form.updateType !== 'NONE' && (
                            <>
                                <TextField
                                    label="업데이트 제목"
                                    value={form.updateTitle}
                                    onChange={(e) => setForm({ ...form, updateTitle: e.target.value })}
                                    fullWidth
                                    inputProps={{ maxLength: 100 }}
                                />
                                <TextField
                                    label="업데이트 메시지"
                                    value={form.updateMessage}
                                    onChange={(e) => setForm({ ...form, updateMessage: e.target.value })}
                                    fullWidth
                                    multiline
                                    rows={3}
                                />
                                <TextField
                                    label="스토어 URL"
                                    value={form.storeUrl}
                                    onChange={(e) => setForm({ ...form, storeUrl: e.target.value })}
                                    fullWidth
                                    placeholder="https://play.google.com/..."
                                />
                            </>
                        )}
                        <TextField
                            label="배포 일시"
                            type="datetime-local"
                            value={form.releasedAt}
                            onChange={(e) => setForm({ ...form, releasedAt: e.target.value })}
                            fullWidth
                            required
                            InputLabelProps={{ shrink: true }}
                        />
                        {dialogMode === 'edit' && (
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={form.isActive}
                                        onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                                    />
                                }
                                label={form.isActive ? '활성' : '비활성'}
                            />
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>취소</Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={isPending || !isFormValid}
                    >
                        {dialogMode === 'create' ? '등록' : '저장'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
